<template>
  <CModal :auto-focus="false" show :to="attachTo" :trap-focus="false" :z-index="zIndex" @keydown.esc="saveAndClose">
    <div
      class="flex flex-col pa-4 gap-4 rounded-t-4 bg-primary6 self-end ma-0 w-full h-[calc(100vh-40px)] slide-in-animation"
      @animationend="autoOpenContentForEmptyGroup"
    >
      <AppHeader :hide-close="hideClose" @close="saveAndClose()" />

      <CWarningAlert
        v-if="translations?.warningMessage"
        v-model:visible="contentWarning.isWarningVisible.value"
        :title="translations?.warningMessage"
      />

      <main class="flex flex-auto flex-col gap-4 overflow-auto">
        <CollectionToolbar :readonlyName="readonlyName" />

        <div class="flex flex-auto flex-col bg-white rounded-2 pa-4 gap-6 overflow-auto relative">
          <VueDraggable
            ref="contentRef"
            class="flex flex-auto flex-col gap-6"
            handle="[data-handle-group]"
            :model-value="collection.groups"
            v-bind="DragConfig"
            @update:model-value="updateGroups"
          >
            <CollectionGroup
              v-for="group in collection.groups"
              :key="group.id"
              :filters="activeFilters"
              :group="group"
            />
          </VueDraggable>

          <!-- Loading overlay -->
          <div
            v-if="isLoadingFiles || !areRequiredFilesLoaded"
            class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10"
          >
            <CSpin :color="themeVars.primary" size="large" />
          </div>
        </div>
      </main>

      <AppFooter @cancel="cancel()" @close="saveAndClose()" />
    </div>
  </CModal>

  <MoveSlideModal
    v-if="moveSlideModal.isOpen.value"
    :groups="collection.groups"
    :z-index="childModalZIndex"
    @moveToGroup="moveSlideToGroup"
  />
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'
import CModal from '@lib/components/CModal/CModal.vue'
import CWarningAlert from '@lib/components/CWarningAlert/CWarningAlert.vue'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'
import type { CollectionPlayerV2, CollectionSlide } from '@collection-player/types/collection-player.type'
import AppHeader from '@collection-selector/components/AppHeader.vue'
import AppFooter from '@collection-selector/components/AppFooter.vue'
import CollectionToolbar from '@collection-selector/components/CollectionToolbar.vue'
import { provideAppStore } from '@collection-selector/stores/app.store'
import { provideTableInput } from '@collection-selector/composables/table-input.use'
import CollectionGroup from '@collection-selector/components/CollectionGroup.vue'
import MoveSlideModal from '@collection-selector/components/modals/MoveSlideModal.vue'
import { provideMoveSlideModal } from '@collection-selector/composables/move-slide-modal.use'
import { DragConfig } from '@collection-selector/constants'
import type { CollectionSelectorProps } from '@collection-selector/types/collection-selector.type'

const props = defineProps<CollectionSelectorProps>()

export type Emit = {
  close: [CollectionPlayerV2]
  settings: [CollectionPlayerV2]
  cancel: []
  contentChange: [CollectionSlide]
}

const emit = defineEmits<Emit>()

provideTableInput()
const confirmation = useConfirmation()
const { t } = useI18n()
const themeVars = useThemeVars()
const moveSlideModal = provideMoveSlideModal()
const isAnimationLoaded = ref(false)
const contentRef = ref<any | null>(null)

const appStore = provideAppStore({
  initialCollection: props.initialData,
  t,
  confirmation,
  selectFileContent: props.selectFileContent,
  fetchFile: props.fetchFile,
  attachTo: props.attachTo,
  openSettings: props.openSettings,
  fetchFileList: props.fetchFileList,
  zIndex: props.zIndex,
  closeOnSettings: props.closeOnSettings,
  translations: props.translations,
  contentRef,
  emit,
})

const {
  moveSlideToGroup,
  updateGroups,
  addSlides,
  saveAndClose,
  cancel,
  collection,
  childModalZIndex,
  contentWarning,
  isLoadingFiles,
  areRequiredFilesLoaded,
} = appStore

const activeFilters = computed(() => collection.value.filters ?? props.initialMetadataFilters)

const autoOpenContentForEmptyGroup = () => {
  if (isAnimationLoaded.value) return
  else if (collection.value.groups.length === 1 && collection.value.groups[0].slides.length === 0) {
    addSlides(collection.value.groups[0].id, activeFilters.value)
  }

  isAnimationLoaded.value = true
}

onMounted(() => {
  contentWarning.validateCollectionPlayerContent(collection.value)
})
</script>

<style lang="scss" scoped>
:deep(.n-collapse-item__header-main) {
  font-size: 15px;
  line-height: 18px !important;
  letter-spacing: 0.15px;
  padding: 8px !important;

  .n-collapse-item-arrow {
    margin-left: 0 !important;
    font-size: 12px !important;
    margin-right: 8px !important;
  }
}

@keyframes slideInFromBottom {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.slide-in-animation {
  animation: slideInFromBottom 0.2s ease-out forwards;
}
</style>
